import {
    Alert, Button, Card, Form, Input, Spin,
} from 'antd';
import {isEqual} from 'lodash-es';
import React, {ChangeEvent, useEffect, useState} from 'react';

import {ConfirmModal} from 'components/confirm-modal/confirm-modal';
import {IconsMap} from 'components/dynamic-icon';
import {FieldTooltip} from 'components/form/field-tooltip';
import {constructLengthErrorMessage} from 'components/form/utils/field-validation-resolver';
import {IFieldTooltipMetadata} from 'entities/metadata/tooltip-metadata-types';
import {URL_LOGIN} from 'shared/constants/urls';
import {useAppHistory} from 'shared/hooks/use-app-history';
import {useUserRoleFunctions} from 'shared/hooks/use-user-role-functions';
import {convertToFormData, escapeRegExp} from 'shared/utils';
import {showMessage} from 'shared/utils/notifications';
import {GeneralSettingsAPI} from 'store/api/general-settings/general-settings.api';
import {useAppDispatch} from 'store/config/hooks';
import {logout} from 'store/slices/auth-slice';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {IGeneralSettingsEntity} from './general-settings-types';

import './general-settings.less';

const GENERAL_WIDTH_BIG = 635;
const GENERAL_WIDTH = 418;
const GENERAL_WIDTH_WITH_TOOLTIP = 444;

type TGeneralData = Omit<IGeneralSettingsEntity, 'currentPassword'> & {
    inputCurrentPassword: string;
}

export const GeneralSettings = () => {
    const [form] = Form.useForm();
    const [alertMessage, setAlertMessage] = useState<string>('');
    const [uploadGeneralSettings] = GeneralSettingsAPI.usePutGeneralSettingsMutation();
    const [isPasswordChanged, setIsPasswordChanged] = useState<boolean>(false);
    const {data: generalSettings} = GeneralSettingsAPI.useFetchGeneralSettingsQuery(null);
    const [initialData, setInitialData] = useState<TGeneralData>();
    const [isConfirmVisible, setIsConfirmVisible] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const history = useAppHistory();

    const escapedAllowedCharacters = escapeRegExp(generalSettings?.availablePasswordCharacters);

    useEffect(() => {
        setInitialData({
            userName: generalSettings?.userName ?? '',
            firstName: generalSettings?.firstName ?? '',
            lastName: generalSettings?.lastName ?? '',
            middleName: generalSettings?.middleName ?? '',
            email: generalSettings?.email ?? '',
            inputCurrentPassword: '',
            password: '',
            confirmPassword: '',
        } as TGeneralData);
    }, [generalSettings]);

    useEffect(() => {
        form.resetFields();
    }, [initialData]);

    const handleAlertClose = () => {
        setAlertMessage('');
    };

    const handleConfirm = async () => {
        const values = form.getFieldsValue();
        setIsConfirmVisible(false);
        handleAlertClose();
        const formValuesConverted: FormData = convertToFormData(values);
        
        try {
            const {data, error}: any = await uploadGeneralSettings(formValuesConverted);
      
            if (error) {
               setAlertMessage(error.data);
               return;
            }

            // Пароль успешно изменён, разрешена смена пароля
            if (data?.passwordChangeFlag === true) {
                showMessage({message: data?.savedStatus, isError: false});
                showMessage({message: data?.passwordChangeMsg, isError: false});
                await dispatch(logout()).unwrap();
                history.push(URL_LOGIN);
            }

            // Пароль не редактировали или запрещена смена пароля
            if (data?.passwordChangeFlag === false) {

                // Пароль редактировали и смена пароля разрешена (в настройке "Включение/выключение блокировки 
                // пользователя по истечению срока пароля" выбрано "нет") - показываем тостер
                if (data?.passwordChangeMsg) {
                    showMessage({message: data?.passwordChangeMsg, isError: true});
                    return;
                }

                setAlertMessage(data?.savedStatus);

                setInitialData(values);
            }
        } catch (err) {
            setAlertMessage(err);
        }
    };

    const handleReset = () => {
        form.resetFields();
        setIsPasswordChanged(false);
        form.validateFields();
    };

    const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsPasswordChanged(event.target.value.length > 0);
    };

    const handleCloseConfirmModal = () => {
        setIsConfirmVisible(false);
        handleReset();
    };

    const {hasUserRoleFunction} = useUserRoleFunctions({
        sections: ['app.settings.user.settings.general'],
    });

    return (
        <Card
            className="general-settings"
            title="Общие"
        >
            <Spin
                spinning={!generalSettings}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={initialData}
                    onFinish={values => {
                        if (!isEqual(values as TGeneralData, initialData as TGeneralData)) {
                            setIsConfirmVisible(true);
                        }
                    }}
                    className="general-settings-form"
                >
                    {alertMessage && (
                        <Alert
                            message={alertMessage || null}
                            type="warning"
                            showIcon
                            closable
                            onClose={handleAlertClose}
                        />
                    )}
                    <div className="general-settings-form__buttons">
                        {hasUserRoleFunction(EUserRoleFunction.CREATE) && (
                            <Button
                                type="primary"
                                onClick={() => form.submit()}
                                icon={<IconsMap.SaveOutlined />}
                            >
                                Сохранить
                            </Button>
                        )}
                        {hasUserRoleFunction(EUserRoleFunction.CANCEL) && (
                            <Button
                                type="default"
                                onClick={handleReset}
                                icon={<IconsMap.CloseXOutlined />}
                            >
                                Отменить
                            </Button>
                        )}
                    </div>
                    <div className="general-settings-form__fields">
                        <Form.Item
                            name="userName"
                            label="Имя пользователя"
                            style={{width: GENERAL_WIDTH_BIG}}
                        >
                            <Input
                                disabled
                                placeholder="Введите имя пользователя"
                            />
                        </Form.Item>
                        <Form.Item
                            name="email"
                            label="Адрес электронной почты"
                            style={{width: GENERAL_WIDTH_BIG}}
                            rules={[
                                {
                                    type: 'email',
                                    message: 'Значение не является корректным почтовым адресом',
                                },
                                {
                                    min: 3,
                                    max: 70,
                                    message: constructLengthErrorMessage({min: 3, max: 70}),
                                },
                            ]}
                        >
                            <Input
                                placeholder="Введите адрес электронной почты"
                            />
                        </Form.Item>
                        <Form.Item
                            name="lastName"
                            label="Фамилия"
                            required
                            style={{width: GENERAL_WIDTH}}
                            validateTrigger={['onBlur']}
                            rules={[
                                {
                                    required: true,
                                    message: 'Поле \'Фамилия\' должно быть заполнено',
                                },
                                {
                                    min: 2,
                                    max: 70,
                                    message: constructLengthErrorMessage({min: 2, max: 70}),
                                },
                                {
                                    pattern: /^[a-zA-ZА-Яа-яЁё\\s,.@_—-]+$/,
                                    message: 'Данное поле должно содержать только буквы и спец символы: _-,.@—',
                                },
                            ]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="firstName"
                            label="Имя"
                            required
                            style={{width: GENERAL_WIDTH}}
                            validateTrigger={['onBlur']}
                            rules={[
                                {
                                    required: true,
                                    message: 'Поле \'Имя\' должно быть заполнено',
                                },
                                {
                                    min: 2,
                                    max: 70,
                                    message: constructLengthErrorMessage({min: 2, max: 70}),
                                },
                                {
                                    pattern: /^[a-zA-ZА-Яа-яЁё\\s,.@_—-]+$/,
                                    message: 'Данное поле должно содержать только буквы и спец символы: _-,.@—',
                                },
                            ]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="middleName"
                            label="Отчество"
                            style={{width: GENERAL_WIDTH}}
                            validateTrigger={['onBlur']}
                            rules={[
                                {
                                    min: 2,
                                    max: 70,
                                    message: constructLengthErrorMessage({min: 2, max: 70}),
                                },
                                {
                                    pattern: /^[a-zA-ZА-Яа-яЁё\\s,.@_—-]+$/,
                                    message: 'Данное поле должно содержать только буквы и спец символы: _-,.@—',
                                },
                            ]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="inputCurrentPassword"
                            label="Текущий пароль"
                            dependencies={['password']}
                            style={{width: GENERAL_WIDTH}}
                            rules={[
                                {
                                    required: isPasswordChanged,
                                    message: 'Поле \'Текущий пароль\' должно быть заполнено',
                                },
                            ]}
                        >
                            <Input.Password
                                placeholder="Введите текущий пароль"
                            />
                        </Form.Item>
                        <Form.Item
                            name="password"
                            label="Новый пароль"
                            style={{width: GENERAL_WIDTH}}
                            validateTrigger={['onBlur']}
                            rules={[
                                {
                                    pattern: new RegExp([
                                        '^(?=.*[a-z])', // хотя бы одна строчная буква
                                        '(?=.*[A-Z])', // хотя бы одна заглавная буква
                                        '(?=.*\\d)', // хотя бы одна цифра
                                        `(?=.*[${escapedAllowedCharacters}])`, // хотя бы один спецсимвол
                                        `[A-Za-z\\d${escapedAllowedCharacters}]+$`, // только разрешенные
                                    ].join('')),
                                    message: `Пароль должен содержать только латинские буквы, не менее одной заглавной, 
                                        одной строчной, одной цифры и одного метасимвола: 
                                        ${generalSettings?.availablePasswordCharacters}`,
                                },
                            ]}
                        >
                            <Input.Password
                                placeholder="Введите новый пароль"
                                onChange={handlePasswordChange}
                            />
                        </Form.Item>
                        <Form.Item
                            name="confirmPassword"
                            label="Подтверждение пароля"
                            dependencies={['password']}
                            hasFeedback
                            style={{width: GENERAL_WIDTH_WITH_TOOLTIP}}
                            className="general-settings_with-tooltip"
                            validateTrigger={['onBlur']}
                            rules={[
                                {
                                    required: isPasswordChanged,
                                    message: 'Поле \'Подтверждение пароля\' должно быть заполнено',
                                },
                                ({getFieldValue}) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Пароли должны совпадать'));
                                    },
                                }),
                            ]}
                            extra={
                                <FieldTooltip
                                    meta={
                                        {contentStructure: 
                                            'После сохранения система перенаправит вас на страницу авторизации',
                                        } as IFieldTooltipMetadata
                                    }
                                />
                            }
                        >
                            <Input.Password placeholder="Введите подтверждение пароля" />
                        </Form.Item>
                    </div>
                </Form>
                <ConfirmModal
                    title="Подтверждение"
                    visible={isConfirmVisible || false}
                    content="Требуется подтвердить изменения"
                    onCancel={handleCloseConfirmModal}
                    cancelText="Нет"
                    onConfirm={handleConfirm}
                    approveText="Да"
                    useWrapperDivElementAsContainer
                />
            </Spin>
        </Card>
    );
};
